import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { selectedSiteAtom } from '../state/sitesAtom';
import { analyticsApi } from '../api';

/**
 * Parse date range input - handles both preset strings and objects with Date instances
 * @param {string|object} dateRange - Either a preset string or { start: Date, end: Date, comparison?: { start: Date, end: Date } }
 * @returns {{ start: Date, end: Date, comparison: { start: Date, end: Date } | null }}
 */
function parseDateRange(dateRange) {
  // If it's an object with start/end dates, use them directly
  if (dateRange && typeof dateRange === 'object' && dateRange.start && dateRange.end) {
    return {
      start: dateRange.start instanceof Date ? dateRange.start : new Date(dateRange.start),
      end: dateRange.end instanceof Date ? dateRange.end : new Date(dateRange.end),
      comparison: dateRange.comparison || null,
    };
  }

  // Handle preset strings
  const end = new Date();
  let start = new Date();
  start.setHours(0, 0, 0, 0);

  switch (dateRange) {
    case 'today':
      break;
    case 'yesterday':
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;
    case '7d':
      start.setDate(start.getDate() - 6);
      break;
    case '30d':
      start.setDate(start.getDate() - 29);
      break;
    case '90d':
      start.setDate(start.getDate() - 89);
      break;
    case '6m':
      start.setMonth(start.getMonth() - 6);
      break;
    case '12m':
      start.setFullYear(start.getFullYear() - 1);
      break;
    default:
      start.setDate(start.getDate() - 6);
  }

  return { start, end, comparison: null };
}

/**
 * Get comparison period dates based on primary range
 */
function getComparisonPeriod(start, end) {
  const duration = end.getTime() - start.getTime();
  const compEnd = new Date(start.getTime() - 1);
  compEnd.setHours(23, 59, 59, 999);
  const compStart = new Date(compEnd.getTime() - duration);
  compStart.setHours(0, 0, 0, 0);
  return { start: compStart, end: compEnd };
}

/**
 * Hook for fetching analytics overview data
 * @param {string|object} dateRange - Date range preset or object
 * @param {boolean} compareEnabled - Enable comparison period
 * @param {boolean} filterBots - Filter out suspected bot traffic
 */
export function useAnalyticsOverview(dateRange = '7d', compareEnabled = false, filterBots = false) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize parsed dates
  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);
  const comparisonDates = useMemo(() => {
    if (!compareEnabled) return null;
    if (dates.comparison) return dates.comparison;
    return getComparisonPeriod(dates.start, dates.end);
  }, [dates, compareEnabled]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
        ...(filterBots && { filterBots: 'true' }),
      };

      // Fetch primary and comparison periods concurrently instead of waterfalling
      const compParams = comparisonDates && {
        site: selectedSite,
        start: comparisonDates.start.toISOString(),
        end: comparisonDates.end.toISOString(),
        ...(filterBots && { filterBots: 'true' }),
      };

      const [result, compResult] = await Promise.all([
        analyticsApi.getOverview(params),
        compParams ? analyticsApi.getOverview(compParams) : Promise.resolve(null),
      ]);

      setData(result);
      setComparisonData(compResult);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates, comparisonDates, filterBots]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, comparisonData, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching traffic data
 * @param {string|object} dateRange - Date range preset or object
 * @param {string} interval - Time interval (hour, day, week)
 * @param {boolean} compareEnabled - Enable comparison period
 * @param {boolean} filterBots - Filter out suspected bot traffic
 */
export function useTrafficData(dateRange = '7d', interval = 'day', compareEnabled = false, filterBots = false) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);
  const comparisonDates = useMemo(() => {
    if (!compareEnabled) return null;
    if (dates.comparison) return dates.comparison;
    return getComparisonPeriod(dates.start, dates.end);
  }, [dates, compareEnabled]);

  // Determine interval based on date range duration
  const effectiveInterval = useMemo(() => {
    const duration = dates.end.getTime() - dates.start.getTime();
    const days = duration / (1000 * 60 * 60 * 24);
    if (days <= 1) return 'hour';
    if (days <= 31) return 'day';
    if (days <= 90) return 'day';
    return 'week';
  }, [dates]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        site: selectedSite,
        interval: effectiveInterval,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
        ...(filterBots && { filterBots: 'true' }),
      };

      const result = await analyticsApi.getTraffic(params);
      setData(result.data || []);

      if (comparisonDates) {
        const compParams = {
          site: selectedSite,
          interval: effectiveInterval,
          start: comparisonDates.start.toISOString(),
          end: comparisonDates.end.toISOString(),
          ...(filterBots && { filterBots: 'true' }),
        };
        const compResult = await analyticsApi.getTraffic(compParams);
        setComparisonData(compResult.data || []);
      } else {
        setComparisonData(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates, comparisonDates, effectiveInterval, filterBots]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, comparisonData, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching top pages
 */
export function useTopPages(dateRange = '7d', limit = 10) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getPages({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
        limit,
      });

      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching traffic sources
 */
export function useTrafficSources(dateRange = '7d') {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getSources({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
      });

      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching device breakdown
 */
export function useDevices(dateRange = '7d') {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getDevices({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
      });

      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching geographic data
 */
export function useGeoData(dateRange = '7d') {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getGeo({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
      });

      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching landing pages data
 */
export function useLandingPages(dateRange = '7d', limit = 10) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getLandingPages({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
        limit,
      });

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching exit pages data
 */
export function useExitPages(dateRange = '7d', limit = 10) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getExitPages({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
        limit,
      });

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching user stickiness data (DAU/WAU/MAU)
 */
export function useStickiness(dateRange = '7d') {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getStickiness({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
      });

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching hourly activity data
 */
export function useHourlyActivity(dateRange = '7d') {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getHourlyActivity({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
      });

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching search keywords data
 */
export function useSearchKeywords(dateRange = '7d', limit = 20) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dates = useMemo(() => parseDateRange(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsApi.getSearchKeywords({
        site: selectedSite,
        start: dates.start.toISOString(),
        end: dates.end.toISOString(),
        limit,
      });

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, dates, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// Export the helper functions for use in other components
export { parseDateRange, getComparisonPeriod };

export default useAnalyticsOverview;
